import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import { guardarConConexion } from '../../../nucleo/api/cliente.js';
import { ConfirmacionGeneral, TONOS_DE_CONFIRMACION } from '../../../nucleo/componentes/ConfirmacionGeneral.jsx';
import { formatearFecha, formatearInstante } from '../../../nucleo/fecha/fechaLocal.js';
import { leerContextoDeRegreso } from '../../../nucleo/navegacion/contextoDeNavegacion.js';
import { rutaDeAltaDesde, useContextoDeRegreso } from '../../../nucleo/navegacion/useContextoDeRegreso.js';
import { BloqueDePlanDelGasto } from '../../msi/componentes/BloqueDePlanDelGasto.jsx';
import { cancelarMovimiento, eliminarMovimiento, obtenerMovimiento, TIPOS } from '../api/movimientosApi.js';
import { importe as formatearImporte, presentacionDelImporte, tituloDelMovimiento } from '../formato.js';
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';
import '../../../estilos/movimientos-eliminacion.css';

/**
 * Detalle de un movimiento (RF-054) y las DOS formas de quitarlo, que no se deben
 * confundir NUNCA (RN-094, RF-207).
 *
 * REGLA DE PANTALLA, LITERAL DE RF-207 (incremento 12/16, prototipo v30):
 *   · «Cancelar movimiento» y «Eliminar» conviven como acciones HERMANAS Y
 *     DISTINTAS, una al lado de la otra, con un recuadro que explica la diferencia
 *     sin jerga antes de los botones.
 *   · «Registrar devolución» NO está en ese grupo: es una CAPTURA, no una forma de
 *     deshacer, y por eso vive en su propio bloque (RN-094b).
 *   · Las dos confirmaciones son DISTINTAS. Si el mismo diálogo sirviera para las
 *     dos, sería defecto (RN-094c).
 *
 * [CAMBIADO 2026-10-07 · 12/16 · RF-207] Hasta el 11/16 el borrado vivía escondido
 * en un menú «Más acciones», porque RN-094a decía que no se ponen dos botones
 * iguales lado a lado. RF-207 y el prototipo v30 lo sacan a la vista: ya NO son dos
 * botones iguales —uno es de aviso y el otro no, y encima van con su explicación—,
 * y la razón de fondo es que eliminar es la salida de emergencia de la captura
 * rápida desde el teléfono: esconderla la volvía inútil.
 *
 * LAS ACCIONES SE OFRECEN DESHABILITADAS, NO OCULTAS, cuando no proceden, y con su
 * razón escrita debajo (RN-028, RF-206). Eso es ayuda visual: el servidor responde
 * 403 o 409 igual si se fuerza la llamada (RNF-018).
 */
export default function DetalleDeMovimiento() {
  const { idMovimiento } = useParams();
  const navegar = useNavigate();

  /*
   * EL REGRESO CONSERVA LOS FILTROS CON LOS QUE SE LLEGÓ (RF-148, RN-390, patrón
   * del incremento 09). El listado pone su dirección exacta en `volverAlContexto`
   * al abrir un movimiento, así que al eliminar se vuelve al MISMO mes, con la
   * MISMA cuenta y con los cancelados igual de visibles, en vez de al mes en curso.
   * Sin ese parámetro —entrando por una dirección pegada a mano— se cae al destino
   * de siempre, que es la conducta de hoy (RN-390c).
   */
  const { destinoDeRegreso } = useContextoDeRegreso('/movimientos');

  /*
   * T-17-03 · H-21 · LA MISMA DIRECCIÓN, HEREDADA TAL CUAL (RF-237, RN-663).
   *
   * Lo que se lee aquí es el `volverAlContexto` QUE ESTA PANTALLA RECIBIÓ del
   * listado, con sus nueve datos de filtro, y es LO MISMO que se le pasa al enlace
   * de Editar más abajo. No se rearma campo por campo a propósito: una dirección
   * reconstruida divergiría en cuanto el filtro gane un dato (RN-663).
   *
   * SE LEE EL PARÁMETRO EN CRUDO Y NO `destinoDeRegreso` porque los dos no son lo
   * mismo: `destinoDeRegreso` ya resolvió la ausencia al destino padre
   * `/movimientos`, y colgarlo en el enlace equivaldría a inventar un contexto que
   * nadie heredó. Sin contexto el enlace va pelado y la edición cae al destino
   * padre, que es RN-666 tal cual (CA-17-10).
   */
  const ubicacion = useLocation();
  const contextoHeredado = leerContextoDeRegreso(ubicacion.search, null);

  const [detalle, setDetalle] = useState(null);
  const [error, setError] = useState(null);
  const [dialogo, setDialogo] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [enCurso, setEnCurso] = useState(false);
  const [resultado, setResultado] = useState(null);

  const cargar = useCallback(async () => {
    try {
      setError(null);
      setDetalle(await obtenerMovimiento(idMovimiento));
    } catch (fallo) {
      setError(fallo);
    }
  }, [idMovimiento]);

  useEffect(() => { cargar(); }, [cargar]);

  const movimiento = detalle?.movimiento;

  /*
   * La ruta del botón Editar, con el contexto heredado colgado cuando existe
   * (RF-237, RN-663) y pelada cuando no (RN-666, CA-17-10).
   */
  const rutaDeEdicion = movimiento
    ? (contextoHeredado
      ? rutaDeAltaDesde(`/movimientos/${movimiento.idMovimiento}/editar`, contextoHeredado)
      : `/movimientos/${movimiento.idMovimiento}/editar`)
    : '#';

  /*
   * Sin `confirmado` el servidor responde CONFIRMACION_REQUERIDA CON EL EFECTO EN
   * EL SALDO YA RESUELTO: el texto del diálogo lo escribe el servidor, no la
   * pantalla, para que la salvaguarda no dependa de que la interfaz se acuerde de
   * pedirla (RN-080e, RN-598).
   *
   * CUALQUIER OTRA RESPUESTA ES UN AVISO ARRIBA DE LA PANTALLA Y NO UN DIÁLOGO: si
   * el movimiento tiene un plan de mensualidades sin cancelar, o si alguna de sus
   * cuentas está desactivada, la pregunta NO SE ABRE (D-304, RF-203). El error no
   * vive dentro de la pregunta; vive donde salen todos los avisos.
   */
  async function pedirConfirmacion(accion) {
    setError(null);
    setMotivo('');
    try {
      if (accion === 'cancelar') {
        await cancelarMovimiento(idMovimiento, { confirmado: false, version: movimiento.version });
      } else {
        await eliminarMovimiento(idMovimiento, { confirmado: false, version: movimiento.version });
      }
    } catch (fallo) {
      if (fallo.codigo === 'CONFIRMACION_REQUERIDA') {
        setDialogo({ accion, mensaje: fallo.mensaje, detalles: fallo.detalles ?? [] });
        return;
      }
      setError(fallo);
    }
  }

  async function confirmar() {
    setError(null);
    setEnCurso(true);
    try {
      if (dialogo.accion === 'cancelar') {
        const respuesta = await guardarConConexion(() => cancelarMovimiento(idMovimiento, {
          confirmado: true,
          motivo: motivo === '' ? null : motivo,
          version: movimiento.version,
        }));
        setResultado({ accion: 'cancelar', respuesta });
        setDialogo(null);
        await cargar();
        return;
      }

      const respuesta = await guardarConConexion(() => eliminarMovimiento(idMovimiento, {
        confirmado: true,
        motivo: motivo === '' ? null : motivo,
        version: movimiento.version,
      }));

      /*
       * TRAS ELIMINAR SE VUELVE AL LISTADO, no se queda una pantalla de detalle de
       * algo que ya no existe (prototipo v30, pantalla 3). Se REEMPLAZA el paso del
       * detalle en el historial: el botón atrás del teléfono no debe devolver a un
       * movimiento inexistente, que respondería «Ese movimiento no existe».
       *
       * El aviso de «listo» y el de la cuenta que se quedó sin movimientos los
       * muestra el listado con lo que viaja aquí: la pantalla de destino no vuelve
       * a preguntar nada al servidor para eso.
       */
      navegar(destinoDeRegreso, {
        replace: true,
        state: {
          movimientoEliminado: respuesta?.idMovimiento ?? Number(idMovimiento),
          cuentasQueVuelvenAEditables: respuesta?.cuentasQueVuelvenAEditables ?? [],
        },
      });
    } catch (fallo) {
      setError(fallo);
      setDialogo(null);
    } finally {
      setEnCurso(false);
    }
  }

  if (error && !movimiento) {
    return (
      <Shell titulo="Detalle del movimiento" volverA={destinoDeRegreso}>
        <AvisoDeError error={error} />
      </Shell>
    );
  }

  if (!detalle) {
    return (
      <Shell titulo="Detalle del movimiento" volverA={destinoDeRegreso}>
        <div className="aviso i">Abriendo el movimiento…</div>
      </Shell>
    );
  }

  const presentacion = presentacionDelImporte(movimiento);
  const puedeEliminar = detalle.puedeEliminar ?? detalle.puedeBorrar;

  return (
    <Shell titulo="Detalle del movimiento" volverA={destinoDeRegreso}>
      {/* ASIDERO ESTABLE DE LA PANTALLA (2026-10-07): el punto fijo al que una prueba
          de punta a punta puede esperar para saber que el detalle ya cargó. Va en la
          raíz y no en una tarjeta concreta, para que siga valiendo cuando el contenido
          cambie. Convención del proyecto: `data-prueba`, nunca `data-testid`. */}
      <div data-prueba="detalle-movimiento">
      <AvisoDeError error={error} />

      <div className="card" style={{ margin: 12, textAlign: 'center' }}>
        <div style={{ fontSize: 30, fontWeight: 700 }}>
          {presentacion.signo}{formatearImporte(movimiento.importe, movimiento.codigoISO)}
        </div>
        <div className="ayuda">
          {movimiento.nombreTipo} · {tituloDelMovimiento(movimiento)} · {formatearFecha(movimiento.fecha)}
        </div>
        {movimiento.cancelado && <span className="chip">CANCELADO</span>}
        {movimiento.esMesesSinIntereses && <span className="chip">MSI</span>}
        {movimiento.esMesesSinIntereses && (
          <div className="ayuda">
            Este es <b>el importe de la compra</b>. El detalle del gasto <b>nunca se reparte</b>: el
            reparto en mensualidades se ve abajo y en el historial.
          </div>
        )}
      </div>

      {/* Un movimiento cancelado TODAVÍA se puede eliminar, y conviene decirlo
          aquí: cancelarlo lo dejó a la vista en «Ver cancelados»; eliminarlo lo
          quita también de ahí (RN-584, RN-594). */}
      {movimiento.cancelado && (
        <div className="aviso e" data-prueba="leyenda-cancelado">
          <b>Cancelado.</b> No cuenta en ningún total y solo se ve en el listado con
          «Ver cancelados» encendido. <b>Todavía se puede eliminar</b>: cancelarlo lo dejó a la
          vista, eliminarlo lo quita también de ahí.
        </div>
      )}

      {/* BLOQUE DEL 4/6 (RF-077.3): su plan, o la acción "Generar plan" si aún no lo
          tiene (RF-074). El 4/6 no reclama un destino propio: cuelga de aquí y de la
          tarjeta (D-62). */}
      <BloqueDePlanDelGasto movimiento={movimiento} />

      <div className="card lista" style={{ margin: 12 }}>
        <Renglon etiqueta="Tipo" valor={movimiento.nombreTipo} />
        {movimiento.nombreCategoria && (
          <Renglon
            etiqueta="Categoría"
            valor={`${movimiento.nombreCategoria}${movimiento.categoriaActiva === false ? ' · desactivada' : ''}`}
          />
        )}
        <Renglon
          etiqueta={movimiento.esTraspaso ? 'Cuenta origen' : 'Cuenta'}
          valor={`${movimiento.nombreCuenta} · ${movimiento.esPropio ? 'tuya' : `de ${movimiento.nombreDuenoCuenta}`}`}
        />
        {movimiento.idCuentaDestino && (
          <Renglon
            etiqueta="Cuenta destino"
            valor={`${movimiento.nombreCuentaDestino} · de ${movimiento.nombreDuenoCuentaDestino}`}
          />
        )}
        <Renglon etiqueta="Moneda" valor={`${movimiento.codigoISO} · la pone la cuenta`} />
        <Renglon etiqueta="Fecha" valor={formatearFecha(movimiento.fecha)} />
        <Renglon etiqueta="Periodo" valor={`${movimiento.idPeriodo} · derivado de la fecha`} />
        <Renglon etiqueta="Lo capturó" valor={movimiento.nombrePropietario} />
        {movimiento.nota && <Renglon etiqueta="Nota" valor={movimiento.nota} />}
        {movimiento.cancelado && (
          <>
            <Renglon etiqueta="Lo canceló" valor={`${movimiento.usuarioCancelacion} · ${formatearInstante(movimiento.fechaCancelacion)}`} />
            {movimiento.motivoCancelacion && <Renglon etiqueta="Motivo" valor={movimiento.motivoCancelacion} />}
          </>
        )}
      </div>

      {/* Los saldos los devuelve el SERVIDOR ya calculados; la pantalla no suma. */}
      <div className="card" style={{ margin: 12 }}>
        <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>SALDO DE SUS CUENTAS</h4>
        {detalle.saldosDeSusCuentas.map((saldo) => (
          <div key={saldo.idCuenta} className="fila">
            <span className="crece">{saldo.nombreCuenta}{saldo.esCredito ? ' · deuda' : ''}</span>
            <b>{formatearImporte(saldo.esCredito ? saldo.deuda : saldo.saldo, saldo.codigoISO)}</b>
          </div>
        ))}
      </div>

      {!movimiento.esPropio && (
        <div className="aviso i" data-prueba="leyenda-ajeno">
          Este movimiento es de <b>{movimiento.nombrePropietario}</b>. Puedes verlo, pero solo esa
          persona puede modificarlo.
        </div>
      )}

      {/* D-308 · El aviso se da ANTES de pulsar, no solo en la confirmación: si
          es el único movimiento de su cuenta, eliminarlo devuelve la cuenta a
          «sin movimientos» y descongela tipo, moneda y saldo inicial (RN-585). */}
      {detalle.esUnicoMovimientoDeSuCuenta && puedeEliminar && (
        <div className="aviso i" data-prueba="aviso-unico-de-su-cuenta">
          <b>Es el único movimiento de «{movimiento.nombreCuenta}».</b> Si lo eliminas, esa cuenta
          vuelve a quedar <b>sin movimientos</b>: podrás volver a cambiarle el tipo, la moneda y el
          importe con el que arrancó. <b>Cancelándolo esto no pasa.</b>
        </div>
      )}

      {/* --- GRUPO 1: corregir. Editar sigue siendo la primera salida. --- */}
      <div style={{ margin: '10px 12px' }} data-prueba="grupo-deshacer">
        <h4 style={{ fontSize: 11.5 }}>Corregir este movimiento</h4>

        {/* T-17-03 · H-21 · AQUÍ ESTABA LA FUGA, Y ERA UN SOLO ENLACE (RF-237,
            RN-663, RN-664). El botón navegaba a `/movimientos/{id}/editar` A SECAS,
            así que la pantalla de edición caía en su destino padre y guardar,
            cancelar y la flecha devolvían al historial PELADO: la persona tenía que
            rearmar su filtro para ver lo que acababa de corregir.
            Se reusa la pieza del núcleo que ya pega este parámetro
            (`rutaDeAltaDesde`, incremento 09) en vez de escribir una segunda forma
            de hacerlo: no hay parámetro nuevo, ni ruta nueva, ni almacenamiento
            nuevo. Es el patrón del 09 aplicado al único enlace que lo esquivaba. */}
        <Link
          className={`btn sec ancho${detalle.puedeEditar ? '' : ' deshabilitado'}`}
          to={detalle.puedeEditar ? rutaDeEdicion : '#'}
          aria-disabled={!detalle.puedeEditar}
          data-prueba="editar-movimiento"
        >
          Editar
        </Link>
      </div>

      {/* --- Las dos salidas, explicadas y luego ofrecidas (RF-207) --- */}
      <div className="salidas" data-prueba="dos-formas-de-quitarlo">
        <h4>Dos formas de quitarlo, y no son lo mismo</h4>
        <div className="salidas__renglon">
          <i className="salidas__marca salidas__marca--cancelar" aria-hidden="true">✕</i>
          <div>
            <b>Cancelar</b>: el movimiento existió y se anula. Deja de contar en tus saldos y en tus
            totales, pero <b>se queda guardado</b> y lo puedes ver cuando enciendes «Ver cancelados».
          </div>
        </div>
        <div className="salidas__renglon">
          <i className="salidas__marca salidas__marca--eliminar" aria-hidden="true">🗑</i>
          <div>
            <b>Eliminar</b>: fue un error de captura. <b>Desaparece</b> de la aplicación como si nunca
            lo hubieras anotado: no sale en la lista, ni en los cancelados, ni en ningún total. No se
            puede recuperar.
          </div>
        </div>
        <div className="salidas__consejo">
          Si dudas, cancela: eso siempre se puede consultar después.
        </div>
      </div>

      <div className="dos-salidas">
        <button
          type="button"
          disabled={!detalle.puedeCancelar}
          onClick={() => pedirConfirmacion('cancelar')}
        >
          {movimiento.cancelado ? 'Ya está cancelado' : 'Cancelar movimiento'}
        </button>

        <button
          className="es-eliminar"
          type="button"
          data-prueba="boton-eliminar"
          disabled={!puedeEliminar}
          onClick={() => pedirConfirmacion('eliminar')}
        >
          Eliminar
        </button>
      </div>

      {/* RF-206 · La razón del bloqueo, a la vista y sin tener que pulsar el botón.
          La escribe el SERVIDOR en lenguaje llano; la pantalla no la deduce. */}
      {!puedeEliminar && detalle.motivoNoEliminable && (
        <div className="razon-del-bloqueo" role="note" data-prueba="razon-no-eliminable">
          <b>No se puede eliminar</b>
          {detalle.motivoNoEliminable}
        </div>
      )}

      {/* --- GRUPO 2, APARTE: la devolución es una CAPTURA (RN-094b). --- */}
      {movimiento.tipo === TIPOS.gasto && movimiento.esPropio && !movimiento.cancelado && (
        <div style={{ margin: '10px 12px' }} data-prueba="grupo-devolucion">
          <h4 style={{ fontSize: 11.5 }}>¿Te devolvieron el dinero? Eso es otra cosa</h4>
          <Link
            className="btn sec ancho"
            to={`/movimientos/devolucion?idCategoria=${movimiento.idCategoria}&idCuenta=${movimiento.idCuenta}`}
          >
            Registrar devolución de esta compra
          </Link>
          {/* Fila 30 del inventario del incremento 15 (RF-222): salió solo la frase «Por eso
              esta acción no está en el grupo de arriba», que justificaba la colocación de un
              botón. El resto del recuadro se conserva. */}
          <div className="aviso s" data-prueba="detalle-ayuda-devolucion">
            Una <b>devolución</b> es <b>dinero que se movió de verdad</b>: crea un <b>movimiento
            nuevo</b> y <b>este gasto se conserva intacto</b>. <b>Cancelar es otra cosa</b>: es corregir
            un registro que no debió contar. Entrando desde aquí, la categoría y la cuenta se copian
            solas y el reembolso <b>resta del gasto de esta categoría</b> en vez de inflar los
            ingresos del mes.
          </div>
        </div>
      )}

      {/* --- Confirmaciones: DOS TEXTOS DISTINTOS, nunca uno compartido. --- */}
      {dialogo?.accion === 'cancelar' && (
        <div style={{ margin: '10px 12px' }} data-prueba="confirmacion-cancelar">
          <div className="aviso w">
            <b>Cancelar el movimiento.</b>
            <p>{dialogo.mensaje}</p>
            <ul>
              <li>El movimiento <b>se conserva</b> en el historial, marcado como cancelado.</li>
              <li>Se ve al encender <b>“Ver cancelados”</b> y <b>deja de contar</b> en los totales.</li>
              <li><b>No se puede descancelar.</b> Si fue un error, se captura de nuevo o se elimina.</li>
            </ul>
          </div>
          <div className="campo">
            <label htmlFor="motivo">MOTIVO (opcional, hasta 500 caracteres)</label>
            <textarea id="motivo" name="motivo" maxLength={500} value={motivo}
              onChange={(evento) => setMotivo(evento.target.value)} />
          </div>
          <button className="btn ancho" type="button" onClick={confirmar}>
            Sí, cancelar el movimiento
          </button>
          <button className="btn sec ancho" type="button" onClick={() => setDialogo(null)}>
            No, dejarlo como está
          </button>
        </div>
      )}

      {dialogo?.accion === 'eliminar' && (
        <ConfirmacionDeEliminar
          movimiento={movimiento}
          dialogo={dialogo}
          motivo={motivo}
          enCurso={enCurso}
          alEscribirMotivo={setMotivo}
          alAceptar={confirmar}
          alCancelar={() => setDialogo(null)}
        />
      )}

      {resultado?.accion === 'cancelar' && (
        <div className="aviso n">
          <b>Movimiento cancelado.</b> Sigue en el historial, marcado, con su fecha, tu nombre y tu
          motivo. Se ve al encender “Ver cancelados”. <b>La cuenta NO descongela su saldo
          inicial</b>: el cancelado sigue contando.
        </div>
      )}
      </div>
    </Shell>
  );
}

/** Tope del motivo, el MISMO de la nota y del motivo de cancelación (RF-204). */
const LONGITUD_MAXIMA_DEL_MOTIVO = 500;

/*
 * LA PREGUNTA ANTES DE ELIMINAR (RF-203, RF-204, RN-598, D-277).
 *
 * ES LA PIEZA ÚNICA DEL NÚCLEO, no un diálogo nuevo: cortina, posición fija, foco
 * atrapado y escape = cancelar vienen de ahí y no se reimplementan (RN-495 a
 * RN-505). Lo que esta pantalla aporta es el CONTENIDO:
 *
 *   · los datos del movimiento del que se pregunta —concepto, fecha, importe y
 *     folio—, que los tiene ya cargados y no vuelve a pedir;
 *   · los renglones que el SERVIDOR mandó en la respuesta CONFIRMACION_REQUERIDA,
 *     TAL CUAL Y EN SU ORDEN: el impacto en el saldo, las dos cuentas de un
 *     traspaso, el aviso de la cuenta que se queda sin movimientos y, cuando el
 *     movimiento ya estaba cancelado, «el saldo no cambia». LA PANTALLA NO DEDUCE
 *     NINGUNA DE ESAS VARIANTES (RN-598, RNF-038): si mañana cambia la regla,
 *     cambia en el servidor y aquí no se toca nada;
 *   · el motivo opcional (RF-204).
 */
function ConfirmacionDeEliminar({
  movimiento, dialogo, motivo, enCurso, alEscribirMotivo, alAceptar, alCancelar,
}) {
  const renglones = useMemo(() => {
    const delServidor = (dialogo.detalles ?? [])
      // `consecuencia` va en su propio sitio y `advertencia` también: aquí quedan
      // las frases del impacto, que son las que forman el recuadro.
      .filter((d) => d.campo !== 'consecuencia' && d.campo !== 'advertencia')
      .map((d, indice) => ({ clave: `${d.campo}-${indice}`, texto: d.mensaje }));

    return [
      { clave: 'concepto', texto: 'Concepto', dato: movimiento.nota || tituloDelMovimiento(movimiento) },
      { clave: 'fecha', texto: 'Fecha', dato: formatearFecha(movimiento.fecha) },
      { clave: 'importe', texto: 'Importe', dato: formatearImporte(movimiento.importe, movimiento.codigoISO) },
      { clave: 'folio', texto: 'Folio', dato: `#${movimiento.idMovimiento}` },
      ...delServidor,
    ];
  }, [dialogo.detalles, movimiento]);

  const consecuencia = (dialogo.detalles ?? []).find((d) => d.campo === 'consecuencia')?.mensaje
    ?? dialogo.mensaje;

  const advertencia = (dialogo.detalles ?? []).find((d) => d.campo === 'advertencia')?.mensaje;

  return (
    <ConfirmacionGeneral
      abierta
      pregunta="¿Eliminar este movimiento?"
      consecuencia={consecuencia}
      detalle={renglones}
      advertencia={advertencia}
      rotuloDeAceptar="Sí, eliminar el movimiento"
      rotuloDeCancelar="No, dejarlo como está"
      rotuloEnCurso="Eliminando…"
      tono={TONOS_DE_CONFIRMACION.DESTRUCTIVA}
      enCurso={enCurso}
      alAceptar={alAceptar}
      alCancelar={alCancelar}
      datoDePrueba="confirmacion-eliminar"
    >
      {/* EL MOTIVO VIVE DENTRO DE LA PREGUNTA, sobre fondo normal y con su propio
          contador, como lo dibuja el prototipo v30 (RF-204). Entra por la ranura de
          contenido que el núcleo ganó para esto el 2026-10-07; antes compartía caja
          con la advertencia, y un campo de captura sobre el ámbar de un aviso se
          leía como parte del aviso. */}
      <div className="motivo-de-eliminacion">
        <label htmlFor="motivo-de-eliminacion">
          ¿Por qué lo eliminas? <span>opcional</span>
        </label>
        <textarea
          id="motivo-de-eliminacion"
          name="motivoDeEliminacion"
          data-prueba="motivo-de-eliminacion"
          maxLength={LONGITUD_MAXIMA_DEL_MOTIVO}
          value={motivo}
          placeholder="Por ejemplo: lo capturé dos veces."
          onChange={(evento) => alEscribirMotivo(evento.target.value)}
        />
        {/* El contador es apoyo visual y no se anuncia: el tope ya lo impone
            `maxLength` y leerlo en voz alta a cada tecla sería ruido (RNF-109). */}
        <div className="motivo-de-eliminacion__cuenta" aria-hidden="true">
          {motivo.length} / {LONGITUD_MAXIMA_DEL_MOTIVO}
        </div>
        <div className="motivo-de-eliminacion__ayuda">
          Puedes dejarlo vacío. Si escribes algo, se guarda con la eliminación por si algún día
          hay que revisarla.
        </div>
      </div>
    </ConfirmacionGeneral>
  );
}

/*
 * RENGLÓN «ETIQUETA A LA IZQUIERDA · VALOR A LA DERECHA».
 *
 * DEFECTO CORREGIDO EL 2026-08-31 (hallazgo de Deisy Paez en el celular): con un
 * valor largo —«Banamex Oro · de DEISY FABIOLA PAEZ ORRANTE»— la etiqueta y el
 * valor se leían MONTADOS UNO SOBRE OTRO. La causa: `.fila` es flex y `.crece`
 * lleva `min-width: 0`, así que la etiqueta se encogía hasta casi cero y su texto
 * DESBORDABA su caja por encima del valor, que a su vez no podía encogerse porque
 * no tenía `min-width: 0`.
 *
 * La corrección conserva el lenguaje visual del prototipo v13 —etiqueta a la
 * izquierda, valor en negrita a la derecha— y solo añade lo que faltaba: los dos
 * pueden encogerse, el renglón puede ENVOLVER, y con un valor largo este baja a su
 * propia línea en vez de pisar la etiqueta. `overflowWrap` evita que una palabra
 * larguísima se salga por el costado en pantalla angosta.
 */
function Renglon({ etiqueta, valor }) {
  return (
    <div className="fila" style={ESTILO_RENGLON}>
      <span className="crece" style={ESTILO_ETIQUETA}>{etiqueta}</span>
      <b style={ESTILO_VALOR}>{valor}</b>
    </div>
  );
}

const ESTILO_RENGLON = { alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 2 };
const ESTILO_ETIQUETA = { flex: '1 1 auto', minWidth: 0, overflowWrap: 'anywhere' };
const ESTILO_VALOR = {
  flex: '1 1 60%', minWidth: 0, textAlign: 'right', overflowWrap: 'anywhere',
};
